import React, { useState, useContext } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { OrganizationContext } from '../contexts/OrganizationContext';
import { removeAuthToken, removeUser } from '../utils/auth';
import { Button } from "./ui/button";
import {
  Home,
  Building2,
  Users,
  Phone,
  FileText,
  CreditCard,
  History,
  Upload,
  Settings,
  Calendar,
  Bot,
  PhoneCall,
  PhoneOutgoing,
  LogOut,
  Menu,
  User,
  PhoneIncoming,
  Smartphone,
  Settings2Icon,
  LibraryBig,
  RefreshCcw,
  Webhook,
  ChartNoAxesCombined,
  MessageSquareCodeIcon,
  MessageSquareCode,
  MessagesSquare,
  BotMessageSquare,
  BookOpenCheck,
  LayoutTemplateIcon,
  GitBranch, 
  Plus, 
  Shield, 
  BarChart 
} from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
} from "./ui/sheet";
import { useSuperAdmin } from '../contexts/SuperAdminContext';
import OrganizationSelector from './OrganizationSelector';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "./ui/tooltip";
import { NotificationBell } from './NotificationBell';
import FloatingChatbot from './FloatingChatbot';

const superAdminLinks = [{
  category: 'Super Admin',
  icon: Shield,
  superAdminOnly: true,
  items: [
    { path: '/super-admin/all-payments', icon: CreditCard, label: 'All Payments' },
    { path: '/super-admin/all-billing', icon: CreditCard, label: 'All Billing' },
    { path: '/super-admin/all-call-summary', icon: FileText, label: 'All Call Summary' },
    { path: '/super-admin/all-call-tracking', icon: Phone, label: 'All Call Tracking' },
    { path: '/super-admin/all-subscriptions', icon: BarChart, label: 'All Subscriptions' },
    { path: '/super-admin/all-phone-numbers', icon: Phone, label: 'All Phone Numbers' },
  ]
},
{
  category: 'Admin',
  icon: Settings,
  superAdminOnly: true, 
  items: [
    { path: '/webhook-sync', icon: Webhook, label: 'Sync Webhook' },
    { path: '/stripe-sync', icon: RefreshCcw, label: 'Stripe Sync' },
    { path: '/vapi-assistants', icon: Bot, label: 'Assistant List' },
    { path: '/platform-email-templates', icon: LayoutTemplateIcon, label: 'Platform Email Templates' }
  ]
}
];

// Group sidebar links by category with category icons
const sidebarLinkGroups = [
  {
    category: 'Main',
    icon: Home,
    items: [
      { path: '/dashboard', icon: ChartNoAxesCombined, label: 'Dashboard' },
      { path: '/contact-management', icon: Users, label: 'Contact Management' }
    ]
  },

/*   {
    category: 'Workflows',
    icon: GitBranch,
    items: [
      { path: '/workflows', icon: GitBranch, label: 'All Workflows' },
      { path: '/workflows/create', icon: Plus, label: 'Create Workflow' }
    ]
  },  
 */  {
    category: 'Settings',
    icon: Settings2Icon,
    items: [
      { path: '/organization', icon: Building2, label: 'Organization' },
      { path: '/appointments', icon: Calendar, label: 'Appointment Settings' },
      { path: '/google-auth', icon: Calendar, label: 'Calendar Authorization' },
      { path: '/assistant-knowledge-base', icon: LibraryBig, label: 'Assistant Knowledge Base' },
      { path: '/crm-integration', icon: BookOpenCheck, label: 'CRM Integration' },
      { path: '/upload-files', icon: Upload, label: 'Upload Files' },
      { path: '/email-configuration', icon: LibraryBig, label: 'Email Configuration' },
      { path: '/email-templates', icon: LayoutTemplateIcon, label: 'Email Templates' },
      { path: '/chatbot', icon: LayoutTemplateIcon, label: 'Chat Configuration' }

      
    ]
  },
  {
    category: 'Subscription & Payments',
    icon: CreditCard,
    items: [
      { path: '/subscriptions', icon: CreditCard, label: 'Subscriptions' },
      { path: '/monthly-billing', icon: CreditCard, label: 'Monthly Billing' },
      { path: '/payment-history-component', icon: History, label: 'Payment History' }
    ]
  },
  {
    category: 'Call Records',
    icon: FileText,
    items: [
      { path: '/themed-call-summary', icon: FileText, label: 'Call Summary' },
      { path: '/themed-call-track', icon: Phone, label: 'Call Tracking' },
      { path: '/sms-conversations', icon: MessagesSquare, label: 'SMS Conversations' }
    ]
  },
  {
    category: 'Calls',
    icon: Phone,
    items: [
      { path: '/vapi-phone-numbers', icon: PhoneIncoming, label: 'Phone Numbers' },
      { path: '/vapi-outbound-calls', icon: PhoneOutgoing, label: 'Outbound Dial' }
    ]
  },
/*   {
    category: 'MultiCnannel Bots',
    icon: Home,
    items: [
      { path: '/phone-manager', icon: Phone, label: 'Phone Manager' },
      { path: '/bot-create', icon: Bot, label: 'Create Bot' },
      { path: '/code-snippet', icon: Bot, label: 'Code Snippet' },
      { path: '/system-prompt-edit', icon: Bot, label: 'System Prompt Editor' },
      { path: '/unified-inbox', icon: Bot, label: 'Unified Inbox' },      
      { path: '/webchat-settings', icon: Bot, label: 'Webchat Settings' },      
      { path: '/webchat-integration', icon: Bot, label: 'Integrate Webchat' },      
      { path: '/webchat-preview', icon: Bot, label: 'Preview Webchat' },      
      { path: '/webchat-customizer', icon: Bot, label: 'Customize Webchat' }      

    ]
  }, */
  {
    category: 'Admin',
    icon: Settings,
    adminOnly: true, 
    items: [
      { path: '/webhook-sync', icon: Webhook, label: 'Sync Webhook' },
      { path: '/vapi-assistants', icon: Bot, label: 'Assistant List' },
      { path: '/platform-email-templates', icon: LayoutTemplateIcon, label: 'Platform Email Templates' }
    ]
  }
];

// Flatten the groups for easy access when needed
const sidebarLinks = sidebarLinkGroups.flatMap(group => group.items);


const Layout = ({ children, isAdmin }) => {
  const { pathname } = useLocation();
  const shouldShowSidebar = pathname !== '/home' && pathname !== '/pricing';
  const { organizationId, navigationEnabled } = useContext(OrganizationContext);
  const { isSuperAdmin } = useSuperAdmin();
  const navigate = useNavigate();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [collapsedGroups, setCollapsedGroups] = useState({});
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);

  // Toggle group collapse
  const toggleGroupCollapse = (groupIndex) => {
    setCollapsedGroups(prev => ({
      ...prev,
      [groupIndex]: !prev[groupIndex]
    }));
  };

  const handleLogout = () => {
    removeAuthToken();
    removeUser();
    navigate('/login');
  };

  // Navigation handler to properly enforce disabled state
  const handleNavigation = (e, path) => {
    if (!navigationEnabled) {
      e.preventDefault();
      console.log('Navigation disabled due to subscription status');
      // Optionally show a message to the user about subscription requirement
    }
  };

  const Sidebar = () => {
    // Create a new array with all link groups
    const allLinkGroups = [...sidebarLinkGroups];
    
    // Add super admin links if the user is a super admin
    if (isSuperAdmin) {
      console.log('Sidebar isSuperAdmin='+isSuperAdmin);
      allLinkGroups.push(...superAdminLinks);
    }

    const filteredGroups = allLinkGroups.filter(group => 
      !group.adminOnly || (group.adminOnly && isAdmin)
    );
    // Move useLocation inside this component
    const sidebarLocation = useLocation();
    console.log('Layout navigationEnabled:', navigationEnabled);
    
    return (
      <div 
        className={`fixed left-0 top-16 bottom-0 transition-all duration-300 ease-in-out
          ${isCollapsed ? 'w-20' : 'w-64'} 
          bg-gradient-to-br from-gray-900/95 to-gray-800/95 backdrop-blur-lg 
          shadow-xl border-r border-gray-700/50 flex flex-col`}
      >
        {/* Scrollable Navigation */}
        <div className="flex-1 overflow-y-auto scrollbar-hide">
          <nav className="p-3 space-y-4">
            {filteredGroups.map((group, groupIndex) => (
              <div key={groupIndex} className="mb-3">
                {!isCollapsed ? (
                  <h3 
                  className="text-xs font-semibold text-gray-400 uppercase tracking-wider ml-3 mb-2 flex items-center cursor-pointer"
                  onClick={() => toggleGroupCollapse(groupIndex)}
                >
                  {React.createElement(group.icon, { className: "h-3.5 w-3.5 mr-1" })}
                  {group.category}
                  <span className="ml-auto">
                    {collapsedGroups[groupIndex] ? 
                      <ChartNoAxesCombined className="h-3 w-3 transform rotate-180" /> : 
                      <ChartNoAxesCombined className="h-3 w-3" />
                    }
                  </span>
                </h3>
                ) : (
                  <div className="flex justify-center mb-2">
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div className="w-8 h-8 flex items-center justify-center rounded-md bg-gray-800">
                            {React.createElement(group.icon, { className: "h-4 w-4 text-gray-400" })}
                          </div>
                        </TooltipTrigger>
                        <TooltipContent side="right">
                          <p>{group.category}</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                )}
                {!collapsedGroups[groupIndex] && (
                  <div className="space-y-1">
                    {group.items.map((link) => {
                      const Icon = link.icon;
                      const isActive = sidebarLocation.pathname === link.path;
                      
                      
                      return (
                        <Link
                          key={link.path}
                          to={navigationEnabled ? link.path : '#'}
                          onClick={(e) => handleNavigation(e, link.path)}
                          className={`flex items-center px-3 py-2 rounded-lg transition-all duration-200
                            ${isActive 
                              ? 'bg-indigo-600 text-white' 
                              : 'text-gray-300 hover:bg-gray-800 hover:text-white'
                            }
                            ${!navigationEnabled ? 'opacity-50 cursor-not-allowed' : ''}
                            ${isCollapsed ? 'justify-center' : ''}`}
                          title={isCollapsed ? `${group.category}: ${link.label}` : ''}
                        >
                          <Icon className={`h-5 w-5 ${isCollapsed ? '' : 'mr-3'}`} />
                          {!isCollapsed && <span className="text-sm font-medium">{link.label}</span>}
                        </Link>
                      );
                    })}
                  </div>
                )}
              </div>
            ))}
          </nav>
        </div>
      </div>
    );
  };

  const MobileSidebar = ({ onClose }) => {
    const filteredGroups = sidebarLinkGroups.filter(group => 
      !group.adminOnly || (group.adminOnly && isAdmin)
    );
    // Add useLocation hook inside this component
    const mobileLocation = useLocation();

    return (
      <div className="space-y-4 py-4">
        {filteredGroups.map((group, groupIndex) => (
          <div key={groupIndex} className="px-3 py-2">
            <h3 
              className="text-xs font-semibold text-gray-400 uppercase tracking-wider ml-3 mb-2 flex items-center cursor-pointer"
              onClick={() => toggleGroupCollapse(groupIndex)}
            >
              {React.createElement(group.icon, { className: "h-3.5 w-3.5 mr-1" })}
              {group.category}
              <span className="ml-auto">
                {collapsedGroups[groupIndex] ? 
                  <ChartNoAxesCombined className="h-3 w-3 transform rotate-180" /> : 
                  <ChartNoAxesCombined className="h-3 w-3" />
                }
              </span>
            </h3>
            {!collapsedGroups[groupIndex] && (
            <div className="space-y-1">
              {group.items.map((link) => {
                const Icon = link.icon;
                const isActive = mobileLocation.pathname === link.path;
                return (
                  <Link
                    key={link.path}
                    to={navigationEnabled ? link.path : '#'}
                    //onClick={(e) => handleNavigation(e, link.path)}
                    onClick={(e) => {
                      handleNavigation(e, link.path);
                      onClose(); // close sidebar on click
                    }}
                    className={`flex items-center px-3 py-2 rounded-lg text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-50
                      ${isActive ? 'bg-gray-100 dark:bg-gray-800 text-indigo-600 dark:text-white' : ''}
                      ${!navigationEnabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <Icon className="h-5 w-5 mr-3" />
                    <span>{link.label}</span>
                  </Link>
                );
              })}
              </div>
            )}
           </div>
        ))}
      </div>
    );
  };


  return (
    <div className="min-h-screen">
      {/* Mobile Sheet */}
      {shouldShowSidebar && (
          <>
          {/* Hamburger Icon */}
          {shouldShowSidebar && (
            <Button
              variant="outline"
              size="icon"
              className="fixed top-4 left-4 z-50 lg:hidden"
              onClick={() => setIsMobileSidebarOpen(true)}
            >
              <Menu className="h-6 w-6" />
            </Button>
          )}
        
          {/* Mobile Sidebar Overlay */}
          {isMobileSidebarOpen && (
            <div className="fixed inset-0 z-50 bg-black bg-opacity-50" onClick={() => setIsMobileSidebarOpen(false)}>
              <div
                className="w-64 h-full bg-gray-900 text-white p-4 overflow-y-auto"
                onClick={(e) => e.stopPropagation()} // Prevent background click from closing immediately
              >
                <MobileSidebar />
              </div>
            </div>
          )}
        </>
      
      )}

      {/* Header */}
      <header className="sticky top-0 z-40 border-b bg-white dark:bg-gray-800">
        <div className="flex h-16 items-center justify-between">
          <div className="flex items-center pl-4">
            {shouldShowSidebar && (
              <Button 
                variant="ghost" 
                size="icon" 
                onClick={() => setIsCollapsed(!isCollapsed)}
                className="mr-4 hidden lg:flex"
              >
                <Menu className="h-6 w-6" />
              </Button>
            )}
            <div className="flex items-center">
              <img
                src="/krupa.jpg"
                alt="Krupa Logo"
                className="h-8 w-auto mr-2"
              />
              <Phone className="h-8 w-8 text-indigo-600" />
{/*               <span className="ml-2 text-xl font-bold text-gray-900 dark:text-white">
                //AI Phone Agent
              </span>
 */}          
              <div className="flex items-center">
              <button
                // onClick={() => onNavigate('home')}
                className="flex items-center space-x-2 text-slate-900 hover:text-blue-600 transition-colors"
              >
                <div className="w-8 h-8 bg-gradient-to-br from-blue-600 to-cyan-500 rounded-lg flex items-center justify-center">
                  <span className="text-white font-bold text-lg">B</span>
                </div>
                <span className="text-xl font-bold">Aigenie</span>
              </button>
            </div>
            </div>
          </div>

          {/* Add the OrganizationSelector here */}
          {isSuperAdmin && <OrganizationSelector isSuperAdmin={isSuperAdmin} />}
          
          <div className="flex items-center gap-4 pr-4">
            <NotificationBell />
            <Link to="/profile">
              <Button variant="ghost" size="icon">
                <User className="h-5 w-5" />
              </Button>
            </Link>
            <Button variant="ghost" size="icon" onClick={handleLogout}>
              <LogOut className="h-5 w-5" />
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="flex">
        {/* Desktop Sidebar */}
        {shouldShowSidebar && <Sidebar />}
    
        {/* Page Content */}
        <main 
          className={`flex-1 p-4 transition-all duration-300 ease-in-out ${
            shouldShowSidebar ? (isCollapsed ? 'ml-20' : 'ml-64') : ''
          }`}
        >
          {children}
        </main>
      </div>
      {/* <FloatingChatbot /> */}
    </div>
  );
};

// Add custom scrollbar styles
const style = document.createElement('style');
style.textContent = `
  .scrollbar-hide::-webkit-scrollbar {
    display: none;
  }
  .scrollbar-hide {
    -ms-overflow-style: none;
    scrollbar-width: none;
  }
`;
document.head.appendChild(style);

export default Layout;